import {
    AfterViewInit,
    Component,
    ElementRef,
    Injector,
    Input,
    inject,
    signal
} from "@angular/core";

import {TranslateService} from '@ngx-translate/core';
import {TilbyToolbar} from "src/app/models";
import _ from 'lodash';
import {mobileCheck} from "@tilby/tilby-ui-lib/utilities";

import {
    DashboardData,
    ObjData,
    SalesDataSentCounts,
    Today
} from "src/app/features/dashboard/models";

import {
    AlertDialogService,
    BestItems,
    BestItemsDialogService,
    ConfirmDialogService,
    DashboardSettingsDialogService,
    FiscalPrinterDashboardDialogService,
    WaitDialogService,
} from "src/app/dialogs";

import {TilbyDatePipe} from "@tilby/tilby-ui-lib/pipes/tilby-date";

import {
    BillingDataService,
    LoaderService,
    UserActiveSessionManagerService,
    ConfigurationManagerService,
    SUPPORT_URLS,
    ToolbarEventsService,
    SystemUtilsService
} from "src/app/core";

import {
    $state,
    restManager,
    util
} from "app/ajs-upgraded-providers";

import { DialogStateService } from "src/app/core/services/dialog.state.service";
import { Channels } from "tilby-models";

import {
    alignDataPrinters,
    getPayments,
    getToday,
    getWeatherIcon,
    DashboardDataManagerService
} from "src/app/features/dashboard";

import { MathUtils } from "src/app/shared/utils";
import { MatDatepickerInputEvent } from "@angular/material/datepicker";

type Consumi = {
    tipo: string;
    denominazione: string;
    idPaese: string;
    idFiscale: string;
    anno: string;
    dataScadenza: string;
    acquistati: string;
    utilizzati: string;
    rimanenti?: number;
    limita: string;
    segnala: string;
}

@Component({
	standalone: false,
    selector: "dashboard",
    templateUrl: "./dashboard.component.html",
    styleUrls: ["./dashboard.component.scss"],
    providers: []
})
export class DashboardComponent implements AfterViewInit, TilbyToolbar {
    private readonly alertDialogService = inject(AlertDialogService);
    private readonly bestItemsDialogService = inject(BestItemsDialogService);
    private readonly billingDataService = inject(BillingDataService);
    private readonly configurationManager = inject(ConfigurationManagerService);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly dashboardDataManagerService = inject(DashboardDataManagerService);
    private readonly dialogStateService = inject(DialogStateService);
    private readonly injector = inject(Injector);
    private readonly fiscalPrinterDashboardDialogService = inject(FiscalPrinterDashboardDialogService);
    private readonly restManagerService = inject(restManager);
    private readonly state = inject($state);
    private readonly tilbyDatePipe = inject(TilbyDatePipe);
    private readonly toolbarEventsService = inject(ToolbarEventsService);
    private readonly translateService = inject(TranslateService);
    private readonly userActiveSession = inject(UserActiveSessionManagerService);
    private readonly utilService = inject(util);
    private readonly systemUtilsService = inject(SystemUtilsService);
    private readonly waitDialogService = inject(WaitDialogService);
    
    protected isMobile = mobileCheck();
    protected adHocCode = this.billingDataService.adHocCode;
    protected supportUrl = SUPPORT_URLS.getSupport;

    dateDashboard : Date = TilbyDatePipe.date({ outputFormat: 'date' });
    today: Date = TilbyDatePipe.date({outputFormat:'date'});
    threeMonthsAgo: Date = this.calculateThreeMonthsAgo();
    isToday: boolean = true;

    hostRef:ElementRef<HTMLElement>=inject(ElementRef);
    @Input() deliverychannels: Channels[] = []; //UIROUTER CONFLICT CON deliveryChannels
    user = this.userActiveSession.getSession()!;

    objData: ObjData = {
        canOpenStocks: this.configurationManager.isModuleEnabled('stock'),
        country: this.configurationManager.getShopCountry(),
        data: {
            date: TilbyDatePipe.date({outputFormat:'date'}),
            today: {} as Today,
            printers: [],
            stocks: {
                alert: 0,
                unavailable: 0,
                available: 0
            },
            e_invoices: {
                error: 0,
                pending: 0
            },
            closings: {
                last_closing: null
            },
            news: [],
            selectedNews: {
                title: null,
                content: null,
                url: null,
                icon: null
            },
            missing_sends: 0,
            progress: {
                current: 0,
                percentage: 0,
                target: 0
            }
        },
        loadCompleted: false,
        userFirstName: this.user.first_name || "",
        thumbnail: this.user.thumbnail || null,
        topbar_context: {
            reloading: Promise.resolve(false)
        }
    };

    protected scrollToReload = true;

    bestItems: BestItems | null = null;
    consumptionInvoices: Consumi | undefined = undefined;
    isLoading = true;
    show_best_products = this.configurationManager.isFunctionEnabledOptin('dashboard.show_best_products');

    eInvoiceSendMode = this.configurationManager.getSetting('e_invoice.send_mode');
    disableEinvoiceStatus: boolean = this.configurationManager.getPreference('dashboard.disable_einvoice_status') || false;
    businessCoachUrl = this.configurationManager.getPreference('businesscoach_embedded_url');
    isAnalyticsEnabled = !!this.configurationManager.getAnalyticsSetting('enable_analytics');
    protected isZucchettiPmsEnabled = this.configurationManager.getPreference('zucchetti_pms.send_other_sales');

    protected salesDataSentCounts: SalesDataSentCounts = {
        count_sales_closed: 0,
        count_sales_pending: 0,
        count_sales_error: 0
    }

    protected onScroll(event:any){
        this.scrollToReload = (event.target?.scrollTop == 0)
    }

    constructor(
    ) {
    }

    async ngOnInit() {
        this.createToolbarButtons();
        this.setModuleTitle();
        this.toolbarEventsService.searchBarValue$.next('');
        this.checkEInvoices();

        let dataCache = this.dashboardDataManagerService.getDataCache();

        if (dataCache) {
            this.refreshView(dataCache);
        }

        this.reloadData();
        this.checkImportingTabacchi();
    }

    private async getSalesDataSentCounts() {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 4);
        const entity = "zpms";

        const URL = `sales_data_sent/counts?closed_at_since=${startDate.toISOString()}&closed_at_max=${endDate.toISOString()}&entity=${entity}`;

        try {
            const counts = await this.restManagerService.getOne(URL);
            if(counts && Object.keys(counts).length) this.salesDataSentCounts = counts;
        } catch(e) {
            // console.error(e);
        }
    }

    private calculateThreeMonthsAgo(): Date {
        const date = new Date();

        date.setMonth(date.getMonth() - 3);

        return date;
    }

    async getConsumi() {
        if (this.eInvoiceSendMode !== 'digitalhub' || this.disableEinvoiceStatus) {
            return;
        }

        try {
            this.consumptionInvoices = await this.restManagerService.getOne('e_invoice/accounts_payable/get_consumi');
            this.checkRemainingInvoices();
        } catch (e) {
            console.log(e);
        }
    }

    checkRemainingInvoices() {
        if(!this.consumptionInvoices) {
            return;
        }

        const result = this.consumptionInvoices;

        let message: string = '';

        result.rimanenti = Number(result.acquistati) - Number(result.utilizzati);

        const quantityWarningPreference = this.configurationManager.getPreference('dashboard.einvoice_quantity_warning');

        if(quantityWarningPreference) {
            const quantityWarning = parseInt(quantityWarningPreference) || 0;

            if(result.rimanenti <= 0) {
                message = 'DASHBOARD.INVOICES_REMAINING_ZERO';
            } else if(result.rimanenti <= quantityWarning) {
                message = this.translateService.instant('DASHBOARD.INVOICES_REMAINING_LESS_THAN_50', { value: result.rimanenti });
            }
        } else {
            if (result.rimanenti <= 0) {
                message = 'DASHBOARD.INVOICES_REMAINING_ZERO';
            } else if (result.rimanenti < 10) {
                message = 'DASHBOARD.INVOICES_REMAINING_LESS_THAN_10';
            } else if (result.rimanenti < 50) {
                message = this.translateService.instant('DASHBOARD.INVOICES_REMAINING_LESS_THAN_50', { value: result.rimanenti });
            }
        }

        if (message) {
            this.alertDialogService.openDialog({
                data: {
                    messageLabel: message,
                    confirmLabel: this.translateService.instant('MISC.OK'),
                }
            });
        }
    }

    getRemainingInvoicesClass() {
        let _class = '';
        if (this.getRemainingInvoices() < 100) {
            _class = 'tw-font-bold';
        }
        if (this.getRemainingInvoices() < 50) {
            _class = 'tw-font-bold tw-text-orange-500';
        }
        if (this.getRemainingInvoices() < 11) {
            _class = 'tw-font-bold tw-text-red-500';
        }
        return _class;
    }

    getRemainingInvoices() {
        return this.consumptionInvoices?.rimanenti || 0;
    }

    ngAfterViewInit(): void {
        this.isLoading=false;
    }

    private setModuleTitle() {
        this.toolbarEventsService.moduleTitle.next("DASHBOARD");
    }

    async checkEInvoices() {
        let result = await this.restManagerService.getOne('sales/e_invoices', 'sync_status');
        _.keys(this.objData.data.e_invoices).forEach((value) => {
            this.objData.data.e_invoices[value as keyof typeof this.objData.data.e_invoices] = _.get(result, ['status', value], 0);
        });
    };

    openBusinessCoach() {
        if(!this.businessCoachUrl) {
            return;
        }

        this.systemUtilsService.openUrlInNewWindow(this.businessCoachUrl, { maximize: true });
    }

    openUrl(url: any) {
        this.utilService.openExternalLink(`${url}?utm_source=scloby-app&utm_medium=dashboard&utm_campaign=dashboard_button`);
    };

    openStocks() {

        this.state.go(this.configurationManager.isModuleAngular('stock.management')
            ? 'app.new.stock.management'
            : 'app.stock.management'
        )
    };

    openSalesDataSent() {this.state.go('app.new.history.module.sales_data_sent')};

    /** @returns false when there was no data to show, so the view was left as it was */
    refreshView(dashboardData: DashboardData): boolean {
        // Some of the requests may have failed: without the main data there is nothing to show
        if (!dashboardData?.today_dashboard?.cash_movements?.total) {
            console.warn('Dashboard data not available');
            return false;
        }

        this.bestItems = dashboardData.today_dashboard.best_items || {};
        let current_cash_movements = dashboardData.today_dashboard.cash_movements;
        let current_cash_in_register = current_cash_movements.total.income.amount + current_cash_movements.total.outcome.amount;

        let weather_icon = getWeatherIcon(dashboardData);

        let payments = getPayments(dashboardData, this.translateService.instant('DASHBOARD.PREPAID_CARDS'));

        this.objData.data.today = getToday(dashboardData, payments, current_cash_movements, current_cash_in_register, weather_icon);

        const goal = parseFloat(this.configurationManager.getPreference('daily_goal') || '') || 1;

        this.objData.data.progress = {
            current: this.objData.data.today.total_amount || 0,
            target: goal,
            percentage: Math.min(100 * (this.objData.data.today.total_amount || 0) / goal, 100) || 0,
            remainder: Math.max(MathUtils.round((this.objData.data.today.total_amount || 0) - goal), 0) || null
        };

        let lastClosing = _.reduce(dashboardData.printers_status, (prev: any, value) => {
            const date = TilbyDatePipe.date({
                outputFormat: 'date',
                date: value.lastUpdate ? new Date(value.lastUpdate) : undefined
            });

            if (prev == null || prev > date) {
                return date;
            }

            return prev;
        }, null);

        this.objData.data.last_closing = (lastClosing === null) ? null : this.tilbyDatePipe.transform(lastClosing);

        this.objData.data.printers = this.dashboardDataManagerService.getMergedResources(dashboardData.today_printers);

        this.objData.data.alignedPrinters = alignDataPrinters(this.objData.data, this.deliverychannels);

        _.reduce(dashboardData.printers_status, (prev, value) => (prev + (_.toInteger(value.rtOldFileToSend) || 0)), 0);

        this.objData.data.stocks = {
            alert: _.filter(dashboardData.stocks, {available: 'alert'}).length || 0,
            unavailable: _.filter(dashboardData.stocks, {available: 'unavailable'}).length || 0,
            available: _.filter(dashboardData.stocks, {available: 'available'}).length || 0
        };

        // check if the selected date is today or not to show the correct translation
        if (this.objData.data.date) {
            const selectedDateTime = this.objData.data.date.getTime();
            
            const now = new Date();

            const todayClean = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const todayTime = todayClean.getTime();
        
            if (selectedDateTime === todayTime) {
                this.isToday = true;
            } else if (selectedDateTime < todayTime) {
                this.isToday = false;
            }
        }

        this.objData.loadCompleted = true;

        return true;
    };

    async reloadData() {
        LoaderService.showLoader();

        await this.getConsumi();

        if (await this.objData.topbar_context.reloading) {
            return;
        }
        this.objData.topbar_context.reloading = new Promise(async (resolve, _reject) => {
            try {
                this.toolbarEventsService.events.next({
                    type: 'toolbar.button_css',
                    action: 'addCssClass',
                    button: 'dashboardRefresh',
                    cssClass: 'rotate-infinite'
                });
                let dashboardData = await this.dashboardDataManagerService.fetchData();
                this.refreshView(dashboardData);
            } catch (err) { console.error(err); }
            this.toolbarEventsService.events.next({
                type: 'toolbar.button_css',
                action: 'removeCssClass',
                button: 'dashboardRefresh',
                cssClass: 'rotate-infinite'
            });
            this.objData.topbar_context.reloading = Promise.resolve(false);
            resolve(false);
        });

        this.objData.topbar_context.reloading.finally(()=>LoaderService.hideLoader());

        this.objData.data.date = this.today;
        this.dateDashboard = this.today;

        if(this.isZucchettiPmsEnabled) {
            await this.getSalesDataSentCounts();
        }
    }

    createToolbarButtons() {
        this.toolbarEventsService.buttons$.next({
            barButtons: [
                {
                    isIt: signal(true),
                    name: 'dashboardRefresh',
                    icon: signal('refresh'),
                    click: _ => this.reloadData()
                },
                {
                    isIt: signal(true),
                    name: 'settings_daily_goal',
                    icon: signal('settings'),
                    click: _ => this.openDashboardSettings()
                }
            ],
            panelButtons: []
        });
    }

    private async openDashboardSettings() {
        const currentDailyGoal = parseFloat(this.configurationManager.getPreference('daily_goal') || '') || 0;
        const currentYearlyProgressive = parseInt(this.configurationManager.getPreference('e_invoice.yearly_progressive') || '') || 0;

        const res = await this.injector.get(DashboardSettingsDialogService).openDialog({
            data: {
                settings: {
                    amount: currentDailyGoal,
                    yearly_progressive: currentYearlyProgressive
                }
            }
        });

        if(!res) {
            return;
        }

        const amount = res.amount || 0;
        this.configurationManager.setShopPreference('daily_goal', amount);

        if(res.yearly_progressive && res.yearly_progressive !== currentYearlyProgressive) {
            this.configurationManager.setShopPreference('e_invoice.yearly_progressive', res.yearly_progressive);
        }

        this.objData.data.progress.target = amount;
        this.objData.data.progress.percentage = Math.min(100 * (this.objData.data.today.total_amount || 0) / amount, 100) || 0;
        this.objData.data.progress.remainder = Math.max(MathUtils.round((this.objData.data.today.total_amount || 0)) - amount, 0) || null;
    }

    async openDetails() {
        if (this.objData.topbar_context.reloading) {
            await this.waitDialogService.openDialog({
                data: {
                    message: "DASHBOARD.WAIT_UPDATING_DATA",
                    promise: this.objData.topbar_context.reloading
                },
                disableClose: true
            });
        }

        await this.fiscalPrinterDashboardDialogService.openDialog({
            data: this.objData.data,
            disableClose: true
        });
    }

    async openBestProducts() {
        const data = {data: {
            title: 'DASHBOARD.DIALOG_BEST_PRODUCTS.TITLE',
            bestItems: this.bestItems
        }};
        await this.bestItemsDialogService.openDialog(data);
    }

    async checkImportingTabacchi() {
        if (!this.dialogStateService.isDialogJustOpen('dialog-import-tobacchi') && this.configurationManager.isModuleEnabled('tobacco_updates')) {
            const confirm = await this.confirmDialogService.openDialog({
                data: {
                    messageLabel: "DIALOG.IMPORT_TABACCHI.TITLE",
                    confirmLabel: "DIALOG.IMPORT_TABACCHI.CONFIRM",
                    cancelLabel: "DIALOG.IMPORT_TABACCHI.CANCEL"
                }
            });
            this.dialogStateService.setDialogJustOpen('dialog-import-tobacchi');
            if (confirm) {
                this.importingTabacchi();
            }
        }
    }



    async importingTabacchi() {
        const result = await this.restManagerService.getList('importing/files');
        const importFiles = result.files.slice().sort((a: { createdAt: Date }, b: { createdAt: Date }) => {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
        let pendingFile = importFiles.find((file : { name: string; status: string;}) => (file.name.includes("Tabacchi") && file.status !== "completed"));
        if(!pendingFile) {
            await this.restManagerService.post('importing/file/tabacchi');
        }
    }

    async setDate(event: MatDatepickerInputEvent<Date>) {
        const selectedDate = event.value;

        if (!selectedDate) {
            return;
        }

        const start = this.configurationManager.getDayStartTime();

        //Change start date to the date selected on the calendar
        start.setFullYear(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    
        const end = new Date(start);
        end.setUTCDate(start.getUTCDate() + 1);
    
        const dateRange = {
            dayStart: start.toISOString(),
            dayEnd: end.toISOString()
        };
    
        // the picker already shows the new date: put the previous one back when its data cannot be shown
        const previousDate = this.objData.data.date;
        const restorePreviousDate = () => {
            this.objData.data.date = previousDate;
            this.dateDashboard = previousDate;
        };

        try {
            const dashboardData = await this.dashboardDataManagerService.fetchData(dateRange);

            this.objData.data.date = selectedDate;

            if (!this.refreshView(dashboardData)) {
                restorePreviousDate();
            }
        } catch (err) {
            console.error(err);
            restorePreviousDate();
        }
    }
}
